import React, { useState, useCallback, useMemo, useEffect } from "react"
import {
  Layout,
  Typography,
  Space,
  Button,
  Empty,
  Row,
  Col,
  Badge,
  Pagination,
  message,
  Spin,
  Alert,
  List,
  Tooltip,
  Progress,
  Modal,
  Select,
} from "antd"
import { SwapOutlined, ArrowLeftOutlined, SortAscendingOutlined } from "@ant-design/icons"
import { People } from "@material-ui/icons"
import CandidateCard from "./CandidateCard"
import PropTypes from 'prop-types';
import cssStyles from './CB.module.css'
import { useHistory } from 'react-router-dom';

import { useSearchPagination } from '../context/searchContext';

const { Text, Title } = Typography
const { Content, Header, Footer } = Layout

const COMPARE_LIMIT = 3;
const CONTAINER_HEIGHT = 600;


const useStyles = {
  mainLayout: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f5f5f5',
    overflow: 'hidden'
  },
  resultsList: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#fff',
    overflow: 'hidden',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  resultsHeader: {
    padding: '16px 24px',
    borderBottom: '1px solid #f0f0f0',
    backgroundColor: '#fff',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
    zIndex: 1,
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px'
  },
  resultsContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '24px',
    backgroundColor: '#fff'
  },
  paginationFooter: {
    padding: '16px 24px',
    borderTop: '1px solid #f0f0f0',
    backgroundColor: '#fff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px'
  },
  badge: {
    '.ant-badge-count': {
      backgroundColor: '#1890ff',
      boxShadow: 'none',
      fontWeight: 600
    }
  },
  compareButton: {
    borderRadius: '6px',
    height: '38px',
    padding: '0 20px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&:not(:disabled):hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(24,144,255,0.25)'
    }
  },
  compareProgress: {
    marginRight: '16px',
  },
  compareLimit: {
    display: 'flex',
    alignItems: 'center',
  },
  virtualListContainer: {
    height: CONTAINER_HEIGHT,
    overflow: 'auto',
    border: '1px solid #e8e8e8',
    borderRadius: '4px',
    padding: '8px',
  }
}

const CBSearchResults = React.memo(({
  results = [],
  loading = false,
  error = null,
  searchQuery = {},
  totalResults = 0,
  onPageChange,
  onCandidateSelect,
  onCompareView,
  onExternalView,
  selectedCandidateId,
  onBackToLanding,
  onSortChange,
}) => {
  const history = useHistory();
  const { pagination, lastViewedCandidate, dispatch } = useSearchPagination();
  const [state, setState] = useState({
    compareList: [],
    viewedCandidates: new Set(),
  });
  
  useEffect(() => {
    try {
      const viewedCandidatesJson = localStorage.getItem('cb_viewed_candidates');
      if (viewedCandidatesJson) {
        const viewedCandidates = new Set(JSON.parse(viewedCandidatesJson));
        setState(prev => ({ ...prev, viewedCandidates }));
      }
    } catch (error) {
      console.error('Error loading viewed candidates from localStorage:', error);
    }
  }, []);

  useEffect(() => {
    if (lastViewedCandidate) {
      const candidateIndex = results.findIndex(c => c.id === lastViewedCandidate);
      if (candidateIndex !== -1) {
        const page = Math.floor(candidateIndex / pagination.pageSize) + 1;
        if (page !== pagination.current) {
          handlePageChange(page, pagination.pageSize);
        }
      }
      dispatch({ type: 'SET_LAST_VIEWED_CANDIDATE', payload: null });
    }
  }, [lastViewedCandidate, results, pagination, dispatch]);

  const markCandidateAsViewed = useCallback((candidateId) => {
    setState(prev => {
      const newViewedCandidates = new Set(prev.viewedCandidates);
      newViewedCandidates.add(candidateId);
      
      try {
        localStorage.setItem('cb_viewed_candidates', JSON.stringify([...newViewedCandidates]));
      } catch (error) {
        console.error('Error saving viewed candidates to localStorage:', error);
      }
      
      return {
        ...prev,
        viewedCandidates: newViewedCandidates
      };
    });
  }, []);

  const handleCandidateSelect = useCallback((candidate) => {
    if (candidate && candidate.id) {
      markCandidateAsViewed(candidate.id);
      dispatch({ type: 'SET_LAST_VIEWED_CANDIDATE', payload: candidate.id });
      onCandidateSelect?.(candidate);
    }
  }, [markCandidateAsViewed, dispatch, onCandidateSelect]);

  const handleCompare = useCallback((e, candidateId) => {
    if (!candidateId) {
      console.warn('Invalid candidate ID for comparison');
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    
    setState(prev => {
      const isAlreadyCompared = prev.compareList.includes(candidateId);
      let newCompareList;

      if (isAlreadyCompared) {
        newCompareList = prev.compareList.filter(id => id !== candidateId);
      } else {
        if (prev.compareList.length >= COMPARE_LIMIT) {
          message.warning(`You can compare up to ${COMPARE_LIMIT} candidates at a time.`);
          return prev;
        }
        newCompareList = [...prev.compareList, candidateId];
      }

      if (!isAlreadyCompared || newCompareList.length >= 2) {
        return {
          ...prev,
          compareList: newCompareList
        };
      }

      Modal.confirm({
        title: 'Remove from comparison?',
        content: 'Removing this candidate will close the comparison view as a minimum of 2 candidates are required. Continue?',
        onOk: () => {
          setState(prevState => ({
            ...prevState,
            compareList: newCompareList
          }));
          onCompareView?.([]); 
        }
      });

      return prev;
    });
  }, [onCompareView]);

  const handleCompareClick = useCallback(() => {
    if (state.compareList.length < 2) {
      message.warning("Please select at least 2 candidates to compare.");
      return;
    }
    const selectedCandidates = results.filter(c => state.compareList.includes(c.id));
    if (selectedCandidates.length !== state.compareList.length) {
      console.warn('Some selected candidates not found in results');
      message.error('Error loading selected candidates');
      return;
    }
    onCompareView?.(selectedCandidates);
  }, [state.compareList, results, onCompareView]);

  const handlePageChange = useCallback((page, pageSize = pagination.pageSize) => {
    dispatch({ 
      type: 'SET_PAGE', 
      payload: { current: page, pageSize } 
    });
    onPageChange?.(page, pageSize);
  }, [pagination.pageSize, dispatch, onPageChange]);

  const handlePageSizeChange = useCallback((current, size) => {
    dispatch({ type: 'SET_PAGE_SIZE', payload: size });
    onPageChange?.(1, size); 
  }, [dispatch, onPageChange]);

  const handleBackToLanding = useCallback(() => {
    if (typeof onBackToLanding === 'function') {
      onBackToLanding();
    } else {
      history.goBack();
    }
  }, [onBackToLanding, history]);

  const sortOptions = [
    { value: "-MODIFIEDINT", label: "Most Recent" },
    { value: "+MODIFIEDINT", label: "Oldest First" },
    { value: "-RELV", label: "Most Relevant" },
    { value: "+RELV", label: "Least Relevant" },
    { value: "-RECENTYEARLYPAY", label: "Highest Salary" },
    { value: "+RECENTYEARLYPAY", label: "Lowest Salary" },
  ];
  
  const handleSortChange = useCallback((value) => {
    if (onSortChange && !loading) {
      onSortChange({ ...searchQuery, sortBy: value });
    }
  }, [onSortChange, searchQuery, loading]);

  const renderCompareButton = useMemo(() => (
    <Space size="large">
      <div style={useStyles.compareLimit}>
        <Progress
          type="circle"
          percent={Math.round((state.compareList.length / COMPARE_LIMIT) * 100)}
          width={40}
          format={() => `${state.compareList.length}/${COMPARE_LIMIT}`}
          style={useStyles.compareProgress}
        />
        <Badge count={state.compareList.length || 0} style={useStyles.badge}>
          <Button
            type="primary"
            onClick={handleCompareClick}
            disabled={state.compareList.length < 2}
            icon={<SwapOutlined />}
            style={useStyles.compareButton}
            className={cssStyles.primaryButton}
          >
            Compare Selected
          </Button>
        </Badge>
      </div>
    </Space>
  ), [state.compareList.length, handleCompareClick]);

  const renderContent = useMemo(() => {
    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Spin size="large" />
        </div>
      );
    }

    if (error) {
      return <Alert message="Error" description={error} type="error" />;
    }

    if (!Array.isArray(results) || !results.length) {
      return (
        <Empty 
          description={
            <div style={{ textAlign: 'center' }}>
              <Text strong>No candidates found</Text>
              <br />
              <Text type="secondary">Try adjusting your search criteria</Text>
            </div>
          }
        />
      );
    }

    return (
      <List
        dataSource={results}
        renderItem={(candidate) => {
          if (!candidate?.id) {
            console.warn('Invalid candidate data:', candidate);
            return null;
          }

          const isViewed = state.viewedCandidates.has(candidate.id) || candidate.isViewed;

          return (
            <List.Item key={candidate.id}>
              <div style={{ width: '100%', padding: '8px 0' }}>
                <CandidateCard
                  candidate={{
                    ...candidate,
                    isViewed
                  }}
                  isSelected={selectedCandidateId === candidate.id}
                  onCardClick={handleCandidateSelect}
                  onCompare={handleCompare}
                  compareList={state.compareList}
                  onAddToDb={() => {}}
                  onExternalView={onExternalView}
                />
              </div>
            </List.Item>
          );
        }}
      />
    );
  }, [
    loading,
    error,
    results,
    pagination,
    selectedCandidateId,
    state.compareList,
    state.viewedCandidates,
    handleCandidateSelect,
    handleCompare,
    onExternalView
  ]);

  if (error) return <Alert message="Error" description={error} type="error" />;

  return (
    <Layout style={useStyles.mainLayout} className={cssStyles.cbContainer}>
      <Header style={useStyles.resultsHeader}>
        <Row justify="space-between" align="middle" gutter={16}>
          <Col>
            <Space size="middle" align="center">
              <Button 
                type="link" 
                icon={<ArrowLeftOutlined />} 
                onClick={handleBackToLanding} 
                className={cssStyles.backButton}
                style={{ marginRight: '8px', padding: '4px 0', color: '#52c41a' }}
              >
                Back
              </Button>
              <People style={{ fontSize: 20, color: '#52c41a' }} />
              <Text strong>{totalResults} Matches Found</Text>
            </Space>
          </Col>
          <Col>
            <Space size="middle">
              <Space>
                <SortAscendingOutlined style={{ color: '#8c8c8c' }} />
                <Select
                  placeholder="Sort by"
                  value={searchQuery.sortBy}
                  onChange={handleSortChange}
                  style={{ width: 180 }}
                  options={sortOptions}
                  disabled={loading}
                  loading={loading}
                />
              </Space>
              {renderCompareButton}
            </Space>
          </Col>
        </Row>
      </Header>

      <Content style={useStyles.resultsContent} className={cssStyles.resultsContent}>
        {renderContent}
      </Content>

      <Footer style={useStyles.paginationFooter}>
        <Pagination
          current={pagination.current}
          total={totalResults}
          pageSize={pagination.pageSize}
          onChange={handlePageChange}
          onShowSizeChange={handlePageSizeChange}
          showSizeChanger
          showQuickJumper
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
        />
      </Footer>
    </Layout>
  );
});

CBSearchResults.propTypes = {
  results: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
  })),
  loading: PropTypes.bool,
  error: PropTypes.string,
  searchQuery: PropTypes.object,
  totalResults: PropTypes.number,
  onPageChange: PropTypes.func,
  onCandidateSelect: PropTypes.func,
  onCompareView: PropTypes.func,
  onExternalView: PropTypes.func,
  selectedCandidateId: PropTypes.string,
  onBackToLanding: PropTypes.func,
  onSortChange: PropTypes.func,
};

CBSearchResults.displayName = 'CBSearchResults';

export default CBSearchResults;